/**
 * Distance effects editor (W2-06; DESIGN.md §5.5 "Distance effects", rpe-format F§12): one block per note control
 * (opacity, width, X and distance multipliers by note distance from the line):
 *  - a graph of the multiplier over the note distance (RPE-y px), the authored curve dashed when Phira ignores it;
 *    the x range covers the finite points only: RPE's far sentinel (9999999 px) is drawn as an arrow to infinity
 *    at the right edge instead of squeezing the meaningful 0-1000 px into a blip (UX-060);
 *  - the points (distance, the curve of the interval ending at the point, value), add / remove; the curve is
 *    picked by name in the easing picker (RPE easing ids only), the sentinel distance shows as infinity;
 *  - presets ("fade in near the line", "shrink far notes", ...), each with 3 points so Phira never ignores it;
 *  - the "inert in Phira (2-point rule)" badge with the one-click fix that adds a third point;
 *  - problem badges from the checker (`line.controls.<which>`).
 * Every edit is one undoable Core.lines.setControl.
 */

import type { AppServices } from '../../../app/registry/types';
import { DIALOG } from '../../../app/dialogs/types';
import { Core } from '../../../core/commands/index';
import type { ControlCurve, ControlPoint, Line, LineId } from '../../../core/model/types';
import { useT } from '../../../i18n/useT';
import type { MessageKey } from '../../../i18n/types';
import { Badge, Button, CurveThumb, IconButton, Menu, NumberField, Tooltip } from '../../../ui';
import type { BadgeProps } from '../../../ui/types';
import { curveText } from '../labels';
import { m } from '../inspector.messages';
import {
  CONTROL_KEYS,
  CONTROL_PRESETS,
  controlCurveAt,
  controlIsInert,
  controlIsNeutral,
  controlNeedsThirdPoint,
  NEUTRAL_CONTROL,
  sortedPoints,
  withThirdPoint,
  type ControlKey,
  type ControlPresetId,
} from '../model';
import { Callout, InfoTip, Readout, Row } from '../parts';
import s from '../Inspector.module.css';
import type { EditKit } from '../tx';
import { controlField } from '../useFieldProblems';

/** Distances from this one on are RPE's "infinitely far" sentinel (it writes 9999999). */
export const FAR_X = 1e6;

const TITLE: Readonly<Record<ControlKey, MessageKey>> = {
  alpha: m.controlAlpha,
  size: m.controlSize,
  pos: m.controlPos,
  y: m.controlY,
};

const DESC: Readonly<Record<ControlKey, MessageKey>> = {
  alpha: m.controlAlphaDesc,
  size: m.controlSizeDesc,
  pos: m.controlPosDesc,
  y: m.controlYDesc,
};

const RPE_KEY: Readonly<Record<ControlKey, string>> = {
  alpha: 'alphaControl',
  size: 'sizeControl',
  pos: 'posControl',
  y: 'yControl',
};

const PRESET_LABEL: Readonly<Record<ControlPresetId, MessageKey>> = {
  fadeInNear: m.presetFadeInNear,
  fadeOutNear: m.presetFadeOutNear,
  shrinkFar: m.presetShrinkFar,
  centreFar: m.presetCentreFar,
  compressFar: m.presetCompressFar,
};

export interface ControlsEditorProps {
  readonly services: AppServices;
  readonly edit: EditKit;
  readonly line: Line;
  readonly badges: (field: string) => readonly BadgeProps[];
}

/** All four distance effects of a line. */
export function ControlsEditor(props: ControlsEditorProps) {
  return (
    <div className={s.fields} data-testid="controls-editor">
      {CONTROL_KEYS.map((which) => (
        <ControlBlock key={which} {...props} which={which} curve={props.line.controls[which]} />
      ))}
    </div>
  );
}

function setControl(edit: EditKit, line: LineId, which: ControlKey, curve: ControlCurve | null, field?: string): void {
  const run = (tx: Parameters<Parameters<EditKit['once']>[1]>[0]): void => {
    Core.lines.setControl(tx, { line, which, curve });
  };
  if (field === undefined) edit.once({ key: m.txControl }, run);
  else edit.commit(field, { key: m.txControl }, run);
}

function ControlBlock(
  props: ControlsEditorProps & { readonly which: ControlKey; readonly curve: ControlCurve | null },
) {
  const { edit, line, which, curve } = props;
  const t = useT();
  const field = controlField(which);
  const presets = CONTROL_PRESETS.filter((p) => p.control === which);
  const presetMenu = (
    <Menu
      label={t(m.controlPresets)}
      trigger={
        <Button size="sm" variant="ghost" icon="Sparkles">
          {t(m.controlPresets)}
        </Button>
      }
      items={presets.map((p) => ({
        kind: 'action' as const,
        label: t(PRESET_LABEL[p.id]),
        onSelect: () => {
          setControl(edit, line.id, which, p.curve);
        },
      }))}
    />
  );
  const inert = curve !== null && controlIsInert(curve) && curve.points.length > 0;
  const needsThird = curve !== null && controlNeedsThirdPoint(curve);

  return (
    <div className={s.controlBlock} data-control={which} data-help-id={field}>
      <div className={s.controlHead}>
        <span>{t(TITLE[which])}</span>
        <InfoTip text={DESC[which]} rpe={RPE_KEY[which]} community="控制" />
        {inert && <Badge kind="phiraIgnores" label={{ key: m.controlInert }} tooltip={{ key: m.controlInertTip }} />}
        {props.badges(field).map((b, i) => (
          <Badge key={i} {...b} />
        ))}
      </div>
      {curve === null ? (
        <>
          <Readout>{t(m.controlNotSet)}</Readout>
          <div className={s.actions}>
            <Button
              size="sm"
              icon="Plus"
              onClick={() => {
                setControl(edit, line.id, which, NEUTRAL_CONTROL);
              }}
            >
              {t(m.controlAdd)}
            </Button>
            {presetMenu}
          </div>
        </>
      ) : (
        <>
          <ControlGraph curve={curve} name={t(TITLE[which])} inert={inert} />
          {needsThird && (
            <Callout
              tone="warning"
              testId={`inert-${which}`}
              actions={
                <Button
                  size="sm"
                  onClick={() => {
                    setControl(edit, line.id, which, withThirdPoint(curve));
                  }}
                >
                  {t(m.addThirdPoint)}
                </Button>
              }
            >
              {t(m.controlInertTip)}
            </Callout>
          )}
          {!needsThird && controlIsNeutral(curve) && <Readout>{t(m.controlNoEffect)}</Readout>}
          <PointList {...props} curve={curve} field={field} />
          <div className={s.actions}>
            <Button
              size="sm"
              icon="Plus"
              onClick={() => {
                // A new point goes after the last finite one (never past the far sentinel).
                const finite = curve.points.filter((p) => p.x < FAR_X);
                const last = finite[finite.length - 1] ?? curve.points[curve.points.length - 1];
                const point: ControlPoint =
                  last === undefined
                    ? { x: 0, easing: 1, value: 1 }
                    : { x: (last.x < FAR_X ? last.x : 0) + 100, easing: 1, value: last.value };
                setControl(edit, line.id, which, { points: sortedPoints([...curve.points, point]) });
              }}
            >
              {t(m.addPoint)}
            </Button>
            {presetMenu}
            <Button
              size="sm"
              variant="ghost"
              icon="Trash2"
              onClick={() => {
                setControl(edit, line.id, which, null);
              }}
            >
              {t(m.controlClear)}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function PointList(
  props: ControlsEditorProps & { readonly which: ControlKey; readonly curve: ControlCurve; readonly field: string },
) {
  const { edit, line, which, curve, field } = props;
  const t = useT();
  const update = (i: number, patch: Partial<ControlPoint>, rowField: string): void => {
    const points = curve.points.map((p, j) => (j === i ? { ...p, ...patch } : p));
    setControl(edit, line.id, which, { points: sortedPoints(points) }, rowField);
  };
  const pickCurve = async (i: number, easing: number, rowField: string): Promise<void> => {
    const result = await props.services.ui.openDialog(DIALOG.easingPicker, {
      segments: [],
      initial: { kind: 'tween', id: easing, left: 0, right: 1 },
      use: 'control',
    });
    if (result === null || result.kind !== 'tween') return;
    update(i, { easing: Math.max(1, Math.min(29, Math.round(result.id))) }, rowField);
  };
  return (
    <div className={s.points}>
      {curve.points.map((p, i) => {
        const rowField = `${field}.${String(i)}`;
        return (
          <Row key={i} edit={edit} field={rowField} className={s.point}>
            {p.x >= FAR_X ? (
              <div className={s.pointInf}>
                <span>{t(m.pointX)}</span>
                <Tooltip content={t(m.pointFar, { x: Math.round(p.x) })}>
                  <span className={s.pointInfValue} role="img" aria-label={t(m.pointFar, { x: Math.round(p.x) })}>
                    {t(m.pointFarShort)}
                  </span>
                </Tooltip>
              </div>
            ) : (
              <NumberField
                label={t(m.pointX)}
                stacked
                value={{ kind: 'value', value: p.x }}
                unit={t(m.px)}
                step={10}
                precision={2}
                onCommit={(v) => {
                  if (v.kind === 'number') update(i, { x: v.value }, rowField);
                }}
              />
            )}
            <div className={s.pointCurve}>
              <span>{t(m.pointEasing)}</span>
              <Tooltip content={curveText(t, { kind: 'tween', id: p.easing, left: 0, right: 1 }, 'full')}>
                <button
                  type="button"
                  className={s.pointCurveButton}
                  aria-label={t(m.pointEasingButton, {
                    curve: curveText(t, { kind: 'tween', id: p.easing, left: 0, right: 1 }, 'full'),
                  })}
                  data-testid={`point-curve-${String(i)}`}
                  onClick={() => {
                    void pickCurve(i, p.easing, rowField);
                  }}
                >
                  <CurveThumb ease={{ kind: 'tween', id: p.easing, left: 0, right: 1 }} size={22} />
                </button>
              </Tooltip>
            </div>
            <NumberField
              label={t(m.pointValue)}
              stacked
              value={{ kind: 'value', value: p.value }}
              unit={t(m.times)}
              step={0.05}
              precision={3}
              onCommit={(v) => {
                if (v.kind === 'number') update(i, { value: v.value }, rowField);
              }}
            />
            <IconButton
              icon="X"
              size="sm"
              label={{ key: m.removePoint }}
              onClick={() => {
                const points = curve.points.filter((_, j) => j !== i);
                setControl(edit, line.id, which, points.length === 0 ? null : { points });
              }}
            />
          </Row>
        );
      })}
    </div>
  );
}

const GW = 280;
const GH = 96;
const PAD = 14;

/** Multiplier over note distance; the authored curve is dashed while Phira ignores it. */
export function ControlGraph(props: { readonly curve: ControlCurve; readonly name: string; readonly inert: boolean }) {
  const t = useT();
  const pts = props.curve.points;
  // The x range covers the finite points; far sentinels are drawn at the right edge as "to infinity".
  const xs = pts.map((p) => p.x).filter((x) => x < FAR_X);
  const far = pts.filter((p) => p.x >= FAR_X);
  const x0 = Math.min(0, ...xs);
  const lastX = Math.max(0, ...xs);
  const x1 = Math.max(900, lastX > 0 ? lastX * 1.15 : 900);
  const samples: Array<readonly [number, number]> = [];
  const n = 64;
  for (let i = 0; i <= n; i++) {
    const x = x0 + ((x1 - x0) * i) / n;
    samples.push([x, controlCurveAt(props.curve, x)]);
  }
  const vals = [...samples.map((p) => p[1]), ...pts.map((p) => p.value), 1];
  const edge = GW - PAD;
  const finite = vals.filter((v) => Number.isFinite(v));
  const y0 = Math.min(0, ...finite);
  const y1 = Math.max(1.2, ...finite.map((v) => v * 1.1));
  const sx = (x: number): number => PAD + ((x - x0) / (x1 - x0)) * (GW - 2 * PAD);
  const sy = (y: number): number => GH - PAD - ((y - y0) / (y1 - y0 || 1)) * (GH - 2 * PAD);
  const path = samples
    .filter(([, y]) => Number.isFinite(y))
    .map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${sx(x).toFixed(1)},${sy(y).toFixed(1)}`)
    .join(' ');
  return (
    <svg
      className={s.graph}
      viewBox={`0 0 ${String(GW)} ${String(GH)}`}
      role="img"
      aria-label={t(m.graphLabel, { name: props.name })}
      data-testid="control-graph"
    >
      <line className={s.graphGrid} x1={PAD} y1={sy(0)} x2={GW - PAD} y2={sy(0)} />
      <line className={s.graphOne} x1={PAD} y1={sy(1)} x2={GW - PAD} y2={sy(1)} />
      <text className={s.graphLabel} x={2} y={sy(1) - 2}>
        1
      </text>
      <text className={s.graphLabel} x={edge} y={GH - 2} textAnchor="end">
        {far.length > 0 ? t(m.graphToFar, { value: Math.round(x1) }) : t(m.pxValue, { value: Math.round(x1) })}
      </text>
      <path className={s.graphCurve} d={path} data-inert={props.inert ? 'true' : 'false'} />
      {pts
        .filter((p) => p.x < FAR_X)
        .map((p, i) => (
          <circle key={i} className={s.graphPoint} cx={sx(p.x)} cy={sy(p.value)} r={3} />
        ))}
      {far.map((p, i) => {
        const y = sy(p.value);
        return (
          <path
            key={`far${String(i)}`}
            className={s.graphFar}
            d={`M${String(edge - 6)},${(y - 4).toFixed(1)} L${String(edge + 2)},${y.toFixed(1)} L${String(edge - 6)},${(y + 4).toFixed(1)} Z`}
            data-testid="graph-far"
          />
        );
      })}
    </svg>
  );
}
